// RLL Video Widget - Embeddable widget script
// Built with esbuild into an IIFE, loaded on external sites via <script> tag.
// Reads data-widget-id from the script tag, fetches settings + data from API,
// and renders a styled video playlist widget inside a Shadow DOM container.

(function () {
    const currentScript = document.currentScript as HTMLScriptElement;
    if (!currentScript) return;

    const widgetId = currentScript.getAttribute('data-widget-id');
    if (!widgetId) {
        console.error('RLL Widget: data-widget-id attribute is required');
        return;
    }

    const apiBase = new URL(currentScript.src).origin;

    // --- Types (inline) ---
    interface Settings {
        behavior: {
            thumbnailsPerPage: number;
            autoPlay: boolean;
            navigation: {
                arrowSize: 'small' | 'medium' | 'large';
                paginationType: 'dots' | 'numbers';
                arrowOpacity: number;
            };
        };
        appearance: {
            colorScheme: string;
            header: { backgroundColor: string; channelNameColor: string; channelHoverColor: string; textColor: string };
            divisionMenu: { backgroundColor: string; textColor: string; hoverColor: string; selectedColor: string };
            navigation: {
                showArrows: boolean;
                showPagination: boolean;
                arrows: { size: string; backgroundColor: string; arrowColor: string };
                pagination: {
                    dotsColor: string; selectedColor: string;
                    numberColor: string; numberSelectedColor: string;
                    numberBackgroundColor: string; numberSelectedBackgroundColor: string;
                };
            };
            body: { backgroundColor: string; titleColor: string; titleHoverColor: string; descriptionColor: string };
        };
    }

    interface ChannelInfo { channelId: string; channelName: string; totalSubscribers: number; totalVideos: number; logoUrl: string }
    interface Division { id: string; name: string; display_order: number; youtube_playlist: string }
    interface Video { id: string; title: string; description: string; thumbnailUrl: string }
    interface ScoreBugData {
        gameId: string;
        visitingScore: number;
        homeScore: number;
        inningText: string;
        outs: number | null;
        visitingTeam: { name: string; logoUrl: string | null; backgroundColor: string };
        homeTeam: { name: string; logoUrl: string | null; backgroundColor: string };
        scoreBugX: number;
        scoreBugY: number;
    }

    // Timecode-aware game-state timeline (mirrors PublicGameTimeline). transitions
    // carry ms-since-video-start (t) with score/inning/half/outs, plus the live
    // count (bl/sk), base occupancy (br bitmask), and the batter/pitcher lines
    // (bi/bo/bh/ba/pi/pp); stateAt() reads the latest transition at or before the
    // current playback position. `roster` resolves batter/pitcher ids to names.
    interface Transition {
        s: number; t: number | null; a: number; h: number; i: number; b: 0 | 1; o: number;
        bl?: number; sk?: number; br?: number;
        bi?: string; bo?: number; bh?: number; ba?: number; pi?: string; pp?: number;
    }
    interface GameTimeline { transitions: Transition[]; roster?: Record<string, { name: string; number: string | null }> }
    interface GameState {
        awayScore: number; homeScore: number; inning: number; isBottom: boolean; outs: number;
        balls: number; strikes: number;
        bases: { first: boolean; second: boolean; third: boolean };
        batter: { order: number | null; name: string | null; hits: number; atBats: number } | null;
        pitcher: { name: string | null; pitches: number } | null;
    }

    // --- State ---
    let settings: Settings | null = null;
    let channelInfo: ChannelInfo | null = null;
    let divisions: Division[] = [];
    let activeStreams: Video[] = [];
    let scoreData: Record<string, ScoreBugData> = {};
    let currentTab = 'live';
    let currentPage = 0;
    let tabVideos: Record<string, Video[]> = {};
    let visibleThumbnails = 4;
    let tabScrollX = 0;
    let shadowRoot: ShadowRoot;
    let rootEl: HTMLElement;

    // --- API helpers ---
    async function fetchSettings(): Promise<Settings> {
        const res = await fetch(`${apiBase}/api/widget/${widgetId}/settings`);
        if (!res.ok) throw new Error('Failed to fetch widget settings');
        return res.json();
    }

    async function fetchData() {
        const res = await fetch(`${apiBase}/api/widget/${widgetId}/data`);
        if (!res.ok) throw new Error('Failed to fetch widget data');
        return res.json();
    }

    async function fetchPlaylist(playlistId: string): Promise<{ videos: Video[]; scoreData: Record<string, ScoreBugData> }> {
        const res = await fetch(`${apiBase}/api/widget/${widgetId}/playlist/${encodeURIComponent(playlistId)}`);
        if (!res.ok) return { videos: [], scoreData: {} };
        return res.json();
    }

    async function fetchTimeline(gameId: string): Promise<GameTimeline | null> {
        try {
            const res = await fetch(`${apiBase}/api/widget/${widgetId}/timeline/${encodeURIComponent(gameId)}`);
            if (!res.ok) return null;
            return await res.json();
        } catch (_) {
            return null;
        }
    }

    // --- Timecode-aware score-bug state (pure; mirrors @/lib/game-timeline) ---
    const INITIAL_STATE: GameState = {
        awayScore: 0, homeScore: 0, inning: 1, isBottom: false, outs: 0,
        balls: 0, strikes: 0, bases: { first: false, second: false, third: false },
        batter: null, pitcher: null,
    };

    // The state to display is the latest transition whose time offset is at or
    // before the playback position. Pure function of position — correct live,
    // when joining late, on archive replay, and while scrubbing. Batter/pitcher
    // ids are resolved to names via the timeline roster here.
    function stateAt(timeline: GameTimeline, positionSeconds: number): GameState {
        const posMs = positionSeconds * 1000;
        const tr = timeline.transitions;
        let lo = 0, hi = tr.length - 1, ans = -1;
        while (lo <= hi) {
            const mid = (lo + hi) >> 1;
            const t = tr[mid].t;
            if (t != null && t <= posMs) { ans = mid; lo = mid + 1; }
            else hi = mid - 1;
        }
        if (ans < 0) return { ...INITIAL_STATE, bases: { ...INITIAL_STATE.bases } };
        const s = tr[ans];
        const roster = timeline.roster || {};
        const br = s.br || 0;
        const batter = (s.bi != null || s.bo != null || s.bh != null || s.ba != null)
            ? { order: s.bo ?? null, name: s.bi ? (roster[s.bi]?.name ?? null) : null, hits: s.bh ?? 0, atBats: s.ba ?? 0 }
            : null;
        const pitcher = (s.pi != null || s.pp != null)
            ? { name: s.pi ? (roster[s.pi]?.name ?? null) : null, pitches: s.pp ?? 0 }
            : null;
        return {
            awayScore: s.a, homeScore: s.h, inning: s.i, isBottom: s.b === 1, outs: s.o,
            balls: s.bl ?? 0, strikes: s.sk ?? 0,
            bases: { first: !!(br & 1), second: !!(br & 2), third: !!(br & 4) },
            batter, pitcher,
        };
    }

    // --- YouTube IFrame API (to read the player's current playback position) ---
    let ytApiLoading: Promise<void> | null = null;
    function loadYouTubeApi(): Promise<void> {
        if ((window as any).YT?.Player) return Promise.resolve();
        if (ytApiLoading) return ytApiLoading;
        ytApiLoading = new Promise<void>((resolve) => {
            const prev = (window as any).onYouTubeIframeAPIReady;
            (window as any).onYouTubeIframeAPIReady = () => { if (typeof prev === 'function') prev(); resolve(); };
            const tag = document.createElement('script');
            tag.src = 'https://www.youtube.com/iframe_api';
            document.head.appendChild(tag);
        });
        return ytApiLoading;
    }

    // --- Helpers ---
    function esc(str: string): string {
        const el = document.createElement('span');
        el.textContent = str;
        return el.innerHTML;
    }

    function getArrowSize(): { icon: number; button: number } {
        const size = settings?.behavior?.navigation?.arrowSize || 'medium';
        if (size === 'small') return { icon: 20, button: 32 };
        if (size === 'large') return { icon: 32, button: 48 };
        return { icon: 24, button: 40 };
    }

    function computeVisibleThumbnails(containerWidth: number): number {
        const gap = 16;
        const minWidth = 200;
        const max = settings?.behavior?.thumbnailsPerPage || 4;
        let count = Math.floor((containerWidth + gap) / (minWidth + gap));
        if (count < 1) count = 1;
        if (count > max) count = max;
        return count;
    }

    function getPageNumbers(cp: number, totalPages: number): (number | 'ellipsis')[] {
        if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i);
        const pages: (number | 'ellipsis')[] = [0];
        if (cp > 2) pages.push('ellipsis');
        for (let i = Math.max(1, cp - 1); i <= Math.min(totalPages - 2, cp + 1); i++) {
            pages.push(i);
        }
        if (cp < totalPages - 3) pages.push('ellipsis');
        pages.push(totalPages - 1);
        return pages;
    }

    // SVG icons
    const chevronLeftSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>`;
    const chevronRightSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>`;
    const playButtonSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 68 48" width="68" height="48"><path d="M66.52 7.74c-.78-2.93-2.49-5.41-5.42-6.19C55.79.13 34 0 34 0S12.21.13 6.9 1.55C3.97 2.33 2.27 4.81 1.48 7.74.06 13.05 0 24 0 24s.06 10.95 1.48 16.26c.78 2.93 2.49 5.41 5.42 6.19C12.21 47.87 34 48 34 48s21.79-.13 27.1-1.55c2.93-.78 4.64-3.26 5.42-6.19C67.94 34.95 68 24 68 24s-.06-10.95-1.48-16.26z" fill="red"/><path d="M45 24L27 14v20" fill="white"/></svg>`;
    const ellipsisSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="16" height="16"><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></svg>`;

    const maximizeSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>`;
    const minimizeSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="14" y1="10" x2="21" y2="3"/><line x1="3" y1="21" x2="10" y2="14"/></svg>`;

    const SCORE_BUG_REFERENCE_WIDTH = 640;

    function outsDotsHtml(outs: number | null): string {
        if (outs == null) return '';
        return `
            <div class="rll-score-bug-outs" aria-label="${outs} out${outs === 1 ? '' : 's'}">
                <span class="rll-score-bug-dot${0 < outs ? ' rll-score-bug-dot-filled' : ''}"></span>
                <span class="rll-score-bug-dot${1 < outs ? ' rll-score-bug-dot-filled' : ''}"></span>
                <span class="rll-score-bug-dot${2 < outs ? ' rll-score-bug-dot-filled' : ''}"></span>
            </div>
        `;
    }

    // Base diamond — 2B top, 1B right, 3B left; a base fills white when occupied.
    function basesDiamondSvg(bases: { first: boolean; second: boolean; third: boolean }): string {
        const r = 4.8;
        const pip = (cx: number, cy: number, on: boolean) =>
            `<path d="M${cx} ${cy - r} L${cx + r} ${cy} L${cx} ${cy + r} L${cx - r} ${cy} Z" fill="${on ? '#fff' : 'none'}" stroke="#fff" stroke-opacity="0.85" stroke-width="1.1"/>`;
        return `<svg width="28" height="25" viewBox="0 0 28 25" aria-hidden="true">${pip(14, 7.5, bases.second)}${pip(20.5, 14, bases.first)}${pip(7.5, 14, bases.third)}</svg>`;
    }

    // Inner markup of the score bug. When `state` is given (timecode-aware), the
    // full bug is shown (scores, inning arrows, count, base diamond, batter/pitcher
    // lines) driven by the video's current position; otherwise the stored fallback
    // (score + inning text + outs) is used (live game with no timeline yet).
    function scoreBugInner(data: ScoreBugData, state: GameState | null): string {
        const visitingLogo = data.visitingTeam.logoUrl ? `<img src="${esc(data.visitingTeam.logoUrl)}" alt="" />` : '';
        const homeLogo = data.homeTeam.logoUrl ? `<img src="${esc(data.homeTeam.logoUrl)}" alt="" />` : '';
        const visitingScore = state ? state.awayScore : data.visitingScore;
        const homeScore = state ? state.homeScore : data.homeScore;
        const outs = state ? state.outs : data.outs;

        // Header: batter (order + name + today's H-for-AB) and pitcher (name + P#).
        let headerHtml = '';
        if (state && (state.batter || state.pitcher)) {
            const batterRow = state.batter ? `
                <div class="rll-sb-line">
                    ${state.batter.order != null ? `<span class="rll-sb-ord">${state.batter.order}</span>` : ''}
                    <span class="rll-sb-name">${esc(state.batter.name || '')}</span>
                    <span class="rll-sb-stat">${state.batter.hits} for ${state.batter.atBats}</span>
                </div>` : '';
            const pitcherRow = state.pitcher ? `
                <div class="rll-sb-line">
                    <span class="rll-sb-pbadge">P</span>
                    <span class="rll-sb-name">${esc(state.pitcher.name || '')}</span>
                    <span class="rll-sb-stat">P: ${state.pitcher.pitches}</span>
                </div>` : '';
            headerHtml = `<div class="rll-score-bug-header">${batterRow}${pitcherRow}</div>`;
        }

        // Inning: up/down arrows around the number (structured) or text + out dots.
        const inningHtml = state ? `
            <div class="rll-score-bug-inning">
                <span class="rll-sb-arrow" style="visibility:${state.isBottom ? 'hidden' : 'visible'}">&#9650;</span>
                <span class="rll-sb-inn">${state.inning}</span>
                <span class="rll-sb-arrow" style="visibility:${state.isBottom ? 'visible' : 'hidden'}">&#9660;</span>
            </div>` : `
            <div class="rll-score-bug-inning">
                <span>${esc(data.inningText)}</span>
                ${outsDotsHtml(outs)}
            </div>`;

        // Situation: base diamond stacked above the count, out dots below.
        const sitHtml = state ? `
            <div class="rll-score-bug-sit">
                ${basesDiamondSvg(state.bases)}
                <span class="rll-sb-count">${state.balls}-${state.strikes}</span>
                ${outsDotsHtml(outs)}
            </div>` : '';

        return `
            ${headerHtml}
            <div class="rll-score-bug-body">
                <div class="rll-score-bug-teams">
                    <div class="rll-score-bug-team" style="background:${esc(data.visitingTeam.backgroundColor)}">
                        <div class="rll-score-bug-logo">${visitingLogo}</div>
                        <div class="rll-score-bug-score">${visitingScore}</div>
                    </div>
                    <div class="rll-score-bug-team" style="background:${esc(data.homeTeam.backgroundColor)}">
                        <div class="rll-score-bug-logo">${homeLogo}</div>
                        <div class="rll-score-bug-score">${homeScore}</div>
                    </div>
                </div>
                ${inningHtml}
                ${sitHtml}
            </div>
        `;
    }

    function renderScoreBug(data: ScoreBugData, containerWidth: number, state: GameState | null = null): string {
        const scale = Math.min(1.2, Math.max(0.4, containerWidth / SCORE_BUG_REFERENCE_WIDTH));
        return `
            <div class="rll-score-bug" style="left:${data.scoreBugX}%;top:${data.scoreBugY}%;transform:scale(${scale})">
                ${scoreBugInner(data, state)}
            </div>
        `;
    }

    // --- CSS ---
    function generateCSS(): string {
        if (!settings) return '';
        const a = settings.appearance;
        const arrowOpacity = (settings.behavior.navigation.arrowOpacity ?? 60) / 100;
        const arrowSizes = getArrowSize();

        return `
            *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
            :host{display:block;font-family:system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;line-height:1.5;-webkit-font-smoothing:antialiased}
            .rll-widget{width:100%;max-width:1200px;margin:0 auto;border-radius:0.5rem;overflow:hidden;box-shadow:0 1px 3px rgba(0,0,0,0.1)}

            /* Header */
            .rll-header{display:flex;align-items:center;justify-content:space-between;padding:1.5rem;background:${a.header.backgroundColor};border-bottom:1px solid ${a.divisionMenu.backgroundColor}}
            .rll-header-left{display:flex;align-items:center;gap:1rem}
            .rll-logo{width:64px;height:64px;border-radius:9999px;object-fit:cover;background:#e5e7eb}
            .rll-channel-name{font-weight:700;font-size:1.5rem;color:${a.header.channelNameColor};text-decoration:none;transition:color 0.2s;display:block}
            .rll-channel-name:hover{color:${a.header.channelHoverColor}}
            .rll-channel-stats{font-size:0.875rem;color:${a.header.textColor}}

            /* Tabs */
            .rll-tabs-wrapper{position:relative;background:${a.divisionMenu.backgroundColor};border-bottom:1px solid rgba(0,0,0,0.1)}
            .rll-tabs-scroll{overflow:hidden;position:relative}
            .rll-tabs{display:flex;padding:0 1.5rem;transition:transform 0.3s ease;min-width:max-content}
            .rll-tab{padding:0.625rem 1rem;font-size:0.875rem;font-weight:500;border:none;border-bottom:2px solid transparent;background:none;cursor:pointer;white-space:nowrap;color:${a.divisionMenu.textColor};transition:color 0.2s;font-family:inherit}
            .rll-tab:hover{color:${a.divisionMenu.hoverColor}}
            .rll-tab.active{color:${a.divisionMenu.selectedColor};border-bottom-color:${a.divisionMenu.selectedColor}}
            .rll-tab-fade-left,.rll-tab-fade-right{position:absolute;top:0;bottom:0;width:4rem;pointer-events:none;z-index:10}
            .rll-tab-fade-left{left:0;background:linear-gradient(to right,${a.divisionMenu.backgroundColor},transparent)}
            .rll-tab-fade-right{right:0;background:linear-gradient(to left,${a.divisionMenu.backgroundColor},transparent)}
            .rll-tab-arrow{position:absolute;top:50%;transform:translateY(-50%);z-index:20;width:2rem;height:2rem;border-radius:9999px;background:#fff;border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;box-shadow:0 1px 3px rgba(0,0,0,0.2);transition:transform 0.15s;padding:0}
            .rll-tab-arrow:hover{transform:translateY(-50%) scale(1.05)}
            .rll-tab-arrow svg{width:1.25rem;height:1.25rem}
            .rll-tab-arrow-left{left:4px}
            .rll-tab-arrow-right{right:4px}

            /* Body */
            .rll-body{padding:1.5rem;background:${a.body.backgroundColor};position:relative}
            .rll-empty{display:flex;align-items:center;justify-content:center;height:320px;color:#9ca3af;font-size:0.875rem}

            /* Video grid */
            .rll-carousel{position:relative;overflow:hidden}
            .rll-pages{display:flex;transition:transform 0.3s ease}
            .rll-page{flex-shrink:0;width:100%;display:grid;gap:1rem}
            .rll-video{width:100%}
            .rll-thumb-wrapper{position:relative;aspect-ratio:16/9;border-radius:0.375rem;overflow:hidden;cursor:pointer;background:#e5e7eb}
            .rll-thumb-wrapper img{width:100%;height:100%;object-fit:cover;transition:transform 0.2s}
            .rll-thumb-wrapper:hover img{transform:scale(1.05)}
            .rll-play-overlay{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.5);opacity:0;transition:opacity 0.2s}
            .rll-thumb-wrapper:hover .rll-play-overlay{opacity:1}
            .rll-video-title{font-weight:500;font-size:0.875rem;line-height:1.4;color:${a.body.titleColor};text-decoration:none;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;margin-top:0.5rem;transition:color 0.2s}
            .rll-video-title:hover{color:${a.body.titleHoverColor}}
            .rll-video-desc{font-size:0.75rem;line-height:1.5;color:${a.body.descriptionColor};display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;margin-top:0.125rem}

            /* Navigation arrows */
            .rll-arrow{position:absolute;z-index:20;border-radius:9999px;display:flex;align-items:center;justify-content:center;border:none;cursor:pointer;background:${a.navigation.arrows.backgroundColor};color:${a.navigation.arrows.arrowColor};opacity:${arrowOpacity};transition:opacity 0.2s;width:${arrowSizes.button}px;height:${arrowSizes.button}px;top:0;transform:translateY(-50%);padding:0}
            .rll-arrow:hover{opacity:1}
            .rll-arrow svg{width:${arrowSizes.icon}px;height:${arrowSizes.icon}px}
            .rll-arrow-left{left:8px}
            .rll-arrow-right{right:8px}

            /* Pagination - dots */
            .rll-pagination{display:flex;justify-content:center;align-items:center;gap:0.5rem;margin-top:1.5rem}
            .rll-dot{width:8px;height:8px;border-radius:9999px;border:none;cursor:pointer;padding:0;transition:all 0.2s;background:${a.navigation.pagination.dotsColor}}
            .rll-dot.active{width:16px;background:${a.navigation.pagination.selectedColor}}

            /* Pagination - numbers */
            .rll-page-num{height:20px;min-width:20px;border-radius:0.25rem;display:flex;align-items:center;justify-content:center;border:none;cursor:pointer;font-size:0.875rem;padding:0 2px;transition:all 0.2s;background:${a.navigation.pagination.numberBackgroundColor};color:${a.navigation.pagination.numberColor};font-family:inherit}
            .rll-page-num.active{background:${a.navigation.pagination.numberSelectedBackgroundColor};color:${a.navigation.pagination.numberSelectedColor}}
            .rll-page-num:disabled{opacity:0.5;cursor:default}
            .rll-page-num svg{width:16px;height:16px}
            .rll-ellipsis{height:20px;width:20px;display:flex;align-items:center;justify-content:center;color:${a.navigation.pagination.numberColor}}

            /* Modal */
            .rll-modal-backdrop{position:fixed;inset:0;z-index:9999;background:rgba(0,0,0,0.7);display:flex;align-items:center;justify-content:center;padding:1rem}
            .rll-modal{background:#fff;border-radius:0.5rem;width:100%;max-width:896px;overflow:hidden;position:relative}
            .rll-modal-close{position:absolute;top:0.5rem;right:0.5rem;z-index:10;width:32px;height:32px;border-radius:9999px;background:rgba(0,0,0,0.5);color:#fff;border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:1.25rem;line-height:1}
            .rll-modal-video{position:relative;width:100%;aspect-ratio:16/9;min-width:200px;min-height:200px}
            .rll-modal-video iframe{position:absolute;inset:0;width:100%;height:100%;border:none}
            .rll-modal-video:fullscreen,.rll-modal-video:-webkit-full-screen{background:#000;width:100vw;height:100vh}
            .rll-modal-video:fullscreen iframe,.rll-modal-video:-webkit-full-screen iframe{position:absolute;inset:0;width:100%;height:100%}
            .rll-modal-video.rll-css-fullscreen{position:fixed;inset:0;z-index:99999;width:100vw;height:100vh;background:#000;aspect-ratio:auto;border-radius:0}
            .rll-modal-video.rll-css-fullscreen iframe{position:absolute;inset:0;width:100%;height:100%}
            .rll-fullscreen-btn{position:absolute;bottom:8px;right:8px;z-index:20;background:rgba(0,0,0,0.6);color:#fff;border:none;border-radius:4px;padding:6px;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:background 0.2s}
            .rll-fullscreen-btn:hover{background:rgba(0,0,0,0.8)}
            .rll-fullscreen-btn svg{width:16px;height:16px}

            /* Score Bug */
            .rll-score-bug{position:absolute;display:inline-flex;flex-direction:column;border-radius:0.375rem;overflow:hidden;box-shadow:0 4px 6px -1px rgba(0,0,0,0.1),0 2px 4px -2px rgba(0,0,0,0.1);pointer-events:none;z-index:10;transform-origin:top left}
            .rll-score-bug-body{display:flex}
            .rll-score-bug-teams{display:flex;flex-direction:column}
            .rll-score-bug-team{display:flex;align-items:center}
            .rll-score-bug-logo{width:40px;height:40px;display:flex;align-items:center;justify-content:center;padding:6px}
            .rll-score-bug-logo img{height:28px;width:28px;object-fit:contain}
            .rll-score-bug-score{width:40px;height:40px;display:flex;align-items:center;justify-content:center;font-weight:700;font-variant-numeric:tabular-nums;color:#fff;background:rgba(0,0,0,0.2)}
            .rll-score-bug-inning{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:0 10px;background:rgba(0,0,0,0.7);font-size:0.75rem;color:#fff;font-weight:500}
            .rll-sb-arrow{font-size:9px;line-height:1;height:10px;color:#fbbf24}
            .rll-sb-inn{font-size:14px;font-weight:700;line-height:1;font-variant-numeric:tabular-nums}
            .rll-score-bug-outs{display:flex;align-items:center;gap:4px;margin-top:2px}
            .rll-score-bug-dot{display:block;width:6px;height:6px;border-radius:9999px;border:1px solid #fff;background:transparent}
            .rll-score-bug-dot-filled{background:#fff}
            .rll-score-bug-header{display:flex;flex-direction:column;gap:2px;background:rgba(0,0,0,0.85);color:#fff;padding:4px 8px}
            .rll-sb-line{display:flex;align-items:baseline;gap:6px;line-height:1}
            .rll-sb-ord{font-size:9px;font-weight:800;color:#000;background:#fbbf24;border-radius:3px;padding:0 4px;font-variant-numeric:tabular-nums}
            .rll-sb-pbadge{font-size:9px;font-weight:800;color:#000;background:rgba(255,255,255,0.75);border-radius:3px;padding:0 4px}
            .rll-sb-name{font-size:11px;font-weight:700;white-space:nowrap;flex:1}
            .rll-sb-stat{font-size:10px;font-weight:600;color:rgba(255,255,255,0.7);font-variant-numeric:tabular-nums;white-space:nowrap}
            .rll-score-bug-sit{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:4px;padding:0 10px;background:rgba(0,0,0,0.6);color:#fff}
            .rll-sb-count{font-size:12px;font-weight:700;line-height:1;font-variant-numeric:tabular-nums}

            /* Loading */
            .rll-loading{display:flex;align-items:center;justify-content:center;padding:3rem;color:#9ca3af}
            .rll-spinner{width:24px;height:24px;border:3px solid #e5e7eb;border-top-color:#6b7280;border-radius:50%;animation:rll-spin 0.6s linear infinite;margin-right:0.5rem}
            @keyframes rll-spin{to{transform:rotate(360deg)}}
        `;
    }

    // --- Render functions ---
    function renderHeader(): string {
        if (!channelInfo) return '';
        const ci = channelInfo;
        const logoHtml = ci.logoUrl
            ? `<img class="rll-logo" src="${esc(ci.logoUrl)}" alt="${esc(ci.channelName)}" />`
            : `<div class="rll-logo"></div>`;

        return `
            <div class="rll-header">
                <div class="rll-header-left">
                    ${logoHtml}
                    <div>
                        <a href="https://youtube.com/channel/${esc(ci.channelId)}" target="_blank" rel="noopener noreferrer" class="rll-channel-name">${esc(ci.channelName)}</a>
                        <div class="rll-channel-stats">${ci.totalSubscribers.toLocaleString()} subscribers - ${ci.totalVideos.toLocaleString()} videos</div>
                    </div>
                </div>
            </div>
        `;
    }

    function getTabScrollMeta(): { canScrollLeft: boolean; canScrollRight: boolean } {
        const scrollEl = rootEl?.querySelector('.rll-tabs-scroll') as HTMLElement;
        const tabsEl = rootEl?.querySelector('.rll-tabs') as HTMLElement;
        if (!scrollEl || !tabsEl) return { canScrollLeft: false, canScrollRight: false };
        const containerW = scrollEl.offsetWidth;
        const contentW = tabsEl.scrollWidth;
        return {
            canScrollLeft: tabScrollX < 0,
            canScrollRight: contentW + tabScrollX > containerW
        };
    }

    function scrollTabs(dir: 'left' | 'right') {
        const scrollEl = rootEl?.querySelector('.rll-tabs-scroll') as HTMLElement;
        const tabsEl = rootEl?.querySelector('.rll-tabs') as HTMLElement;
        if (!scrollEl || !tabsEl) return;
        const containerW = scrollEl.offsetWidth;
        const contentW = tabsEl.scrollWidth;
        const step = containerW * 0.6;
        if (dir === 'left') {
            tabScrollX = Math.min(0, tabScrollX + step);
        } else {
            const maxScroll = -(contentW - containerW);
            tabScrollX = Math.max(maxScroll, tabScrollX - step);
        }
        tabsEl.style.transform = `translateX(${tabScrollX}px)`;
        updateTabArrows();
    }

    function updateTabArrows() {
        const meta = getTabScrollMeta();
        const wrapper = rootEl?.querySelector('.rll-tabs-wrapper');
        if (!wrapper) return;
        const leftFade = wrapper.querySelector('.rll-tab-fade-left') as HTMLElement;
        const rightFade = wrapper.querySelector('.rll-tab-fade-right') as HTMLElement;
        const leftArrow = wrapper.querySelector('.rll-tab-arrow-left') as HTMLElement;
        const rightArrow = wrapper.querySelector('.rll-tab-arrow-right') as HTMLElement;
        if (leftFade) leftFade.style.display = meta.canScrollLeft ? '' : 'none';
        if (rightFade) rightFade.style.display = meta.canScrollRight ? '' : 'none';
        if (leftArrow) leftArrow.style.display = meta.canScrollLeft ? '' : 'none';
        if (rightArrow) rightArrow.style.display = meta.canScrollRight ? '' : 'none';
    }

    function renderTabs(): string {
        const liveCount = activeStreams.length > 0 ? ` (${activeStreams.length})` : '';
        let tabs = `<button class="rll-tab${currentTab === 'live' ? ' active' : ''}" data-tab="live">Live Now${liveCount}</button>`;
        for (const d of divisions) {
            const label = d.name.replace(' Division', '');
            tabs += `<button class="rll-tab${currentTab === d.id ? ' active' : ''}" data-tab="${esc(d.id)}">${esc(label)}</button>`;
        }
        return `
            <div class="rll-tabs-wrapper">
                <div class="rll-tab-fade-left" style="display:none"></div>
                <button class="rll-tab-arrow rll-tab-arrow-left" data-tab-scroll="left" style="display:none">${chevronLeftSvg}</button>
                <div class="rll-tabs-scroll">
                    <div class="rll-tabs" style="transform:translateX(${tabScrollX}px)">${tabs}</div>
                </div>
                <div class="rll-tab-fade-right" style="display:none"></div>
                <button class="rll-tab-arrow rll-tab-arrow-right" data-tab-scroll="right" style="display:none">${chevronRightSvg}</button>
            </div>
        `;
    }

    function renderVideoGrid(): string {
        const videos = currentTab === 'live' ? activeStreams : (tabVideos[currentTab] || []);
        const emptyMessage = currentTab === 'live'
            ? 'No games streaming live at the moment. Choose a game from the archive by selecting a tab above.'
            : 'No videos available';
        if (videos.length === 0) return `<div class="rll-empty">${esc(emptyMessage)}</div>`;

        const totalPages = Math.ceil(videos.length / visibleThumbnails);
        const cols = `repeat(${visibleThumbnails}, 1fr)`;
        let pagesHtml = '';
        for (let p = 0; p < totalPages; p++) {
            const pageVideos = videos.slice(p * visibleThumbnails, (p + 1) * visibleThumbnails);
            let videosHtml = '';
            for (const v of pageVideos) {
                const thumbSrc = v.thumbnailUrl || '';
                videosHtml += `
                    <div class="rll-video">
                        <div class="rll-thumb-wrapper" data-video-id="${esc(v.id)}" data-video-title="${esc(v.title)}">
                            ${thumbSrc ? `<img src="${esc(thumbSrc)}" alt="${esc(v.title)}" loading="lazy" />` : ''}
                            <div class="rll-play-overlay">${playButtonSvg}</div>
                        </div>
                        <a href="https://youtube.com/watch?v=${esc(v.id)}" target="_blank" rel="noopener noreferrer" class="rll-video-title">${esc(v.title)}</a>
                        <div class="rll-video-desc">${esc(v.description || '')}</div>
                    </div>
                `;
            }
            pagesHtml += `<div class="rll-page" style="grid-template-columns:${cols}">${videosHtml}</div>`;
        }

        return `<div class="rll-carousel"><div class="rll-pages" style="transform:translateX(-${currentPage * 100}%)">${pagesHtml}</div></div>`;
    }

    function renderNavigation(): string {
        if (!settings) return '';
        const videos = currentTab === 'live' ? activeStreams : (tabVideos[currentTab] || []);
        const totalPages = Math.ceil(videos.length / visibleThumbnails);
        if (totalPages <= 1) return '';

        let html = '';

        // Arrows
        if (settings.appearance.navigation.showArrows) {
            const carouselEl = rootEl?.querySelector('.rll-carousel') as HTMLElement;
            const thumbHeight = carouselEl ? carouselEl.offsetWidth / visibleThumbnails * 9 / 16 : 180;
            const arrowTop = `${thumbHeight / 2}px`;

            if (currentPage > 0) {
                html += `<button class="rll-arrow rll-arrow-left" data-arrow="left" style="top:${arrowTop}">${chevronLeftSvg}</button>`;
            }
            if (currentPage < totalPages - 1) {
                html += `<button class="rll-arrow rll-arrow-right" data-arrow="right" style="top:${arrowTop}">${chevronRightSvg}</button>`;
            }
        }

        // Pagination
        if (settings.appearance.navigation.showPagination) {
            const pType = settings.behavior.navigation.paginationType;

            if (pType === 'dots') {
                let dots = '';
                for (let i = 0; i < totalPages; i++) {
                    dots += `<button class="rll-dot${i === currentPage ? ' active' : ''}" data-page="${i}"></button>`;
                }
                html += `<div class="rll-pagination">${dots}</div>`;
            } else {
                const pages = getPageNumbers(currentPage, totalPages);
                let nums = `<button class="rll-page-num" data-page="${currentPage - 1}" ${currentPage === 0 ? 'disabled' : ''}>${chevronLeftSvg}</button>`;
                for (const p of pages) {
                    if (p === 'ellipsis') {
                        nums += `<div class="rll-ellipsis">${ellipsisSvg}</div>`;
                    } else {
                        nums += `<button class="rll-page-num${p === currentPage ? ' active' : ''}" data-page="${p}">${p + 1}</button>`;
                    }
                }
                nums += `<button class="rll-page-num" data-page="${currentPage + 1}" ${currentPage === totalPages - 1 ? 'disabled' : ''}>${chevronRightSvg}</button>`;
                html += `<div class="rll-pagination">${nums}</div>`;
            }
        }

        return html;
    }

    // --- Main render ---
    function render() {
        if (!rootEl || !settings) return;

        rootEl.innerHTML = `
            <div class="rll-widget">
                ${renderHeader()}
                ${renderTabs()}
                <div class="rll-body">
                    ${renderVideoGrid()}
                    ${renderNavigation()}
                </div>
            </div>
        `;

        attachEventListeners();
        // Update tab scroll arrows after render (needs DOM to be present)
        requestAnimationFrame(() => updateTabArrows());
    }

    function showLoading() {
        rootEl.innerHTML = `<div class="rll-loading"><div class="rll-spinner"></div>Loading...</div>`;
    }

    function showError() {
        rootEl.innerHTML = `<div class="rll-loading" style="color:#ef4444">Widget temporarily unavailable</div>`;
    }

    // --- Events ---
    function attachEventListeners() {
        // Tab clicks
        rootEl.querySelectorAll('.rll-tab').forEach(tab => {
            tab.addEventListener('click', async () => {
                const tabId = (tab as HTMLElement).getAttribute('data-tab');
                if (!tabId || tabId === currentTab) return;
                currentTab = tabId;
                currentPage = 0;

                // Lazy-load playlist videos (and their score-bug data, so the
                // score bug renders on archived video too)
                if (tabId !== 'live' && !tabVideos[tabId]) {
                    const div = divisions.find(d => d.id === tabId);
                    if (div?.youtube_playlist) {
                        const res = await fetchPlaylist(div.youtube_playlist);
                        tabVideos[tabId] = res.videos;
                        scoreData = { ...scoreData, ...res.scoreData };
                    } else {
                        tabVideos[tabId] = [];
                    }
                }

                render();
            });
        });

        // Tab scroll arrow clicks
        rootEl.querySelectorAll('[data-tab-scroll]').forEach(btn => {
            btn.addEventListener('click', () => {
                const dir = (btn as HTMLElement).getAttribute('data-tab-scroll') as 'left' | 'right';
                scrollTabs(dir);
            });
        });

        // Arrow clicks
        rootEl.querySelectorAll('.rll-arrow').forEach(arrow => {
            arrow.addEventListener('click', () => {
                const dir = (arrow as HTMLElement).getAttribute('data-arrow');
                if (dir === 'left' && currentPage > 0) {
                    currentPage--;
                    render();
                } else if (dir === 'right') {
                    const videos = currentTab === 'live' ? activeStreams : (tabVideos[currentTab] || []);
                    const totalPages = Math.ceil(videos.length / visibleThumbnails);
                    if (currentPage < totalPages - 1) {
                        currentPage++;
                        render();
                    }
                }
            });
        });

        // Pagination clicks
        rootEl.querySelectorAll('[data-page]').forEach(btn => {
            btn.addEventListener('click', () => {
                if ((btn as HTMLButtonElement).disabled) return;
                const page = parseInt((btn as HTMLElement).getAttribute('data-page') || '0', 10);
                if (page >= 0) {
                    const videos = currentTab === 'live' ? activeStreams : (tabVideos[currentTab] || []);
                    const totalPages = Math.ceil(videos.length / visibleThumbnails);
                    if (page < totalPages) {
                        currentPage = page;
                        render();
                    }
                }
            });
        });

        // Video thumbnail clicks - open modal
        rootEl.querySelectorAll('.rll-thumb-wrapper').forEach(thumb => {
            thumb.addEventListener('click', (e) => {
                e.preventDefault();
                const videoId = (thumb as HTMLElement).getAttribute('data-video-id');
                const videoTitle = (thumb as HTMLElement).getAttribute('data-video-title') || 'Video';
                if (videoId) openVideoModal(videoId, videoTitle);
            });
        });
    }

    function openVideoModal(videoId: string, title: string) {
        const autoplay = settings?.behavior?.autoPlay ? '1' : '0';
        // Score bug shows whenever we have data for this video — live or archived.
        const scoreBug = scoreData[videoId] ? scoreData[videoId] : null;
        const isMobile = typeof window !== 'undefined' && window.matchMedia('(hover: none) and (pointer: coarse)').matches;
        const fsParam = isMobile ? '1' : '0';
        const backdrop = document.createElement('div');
        backdrop.className = 'rll-modal-backdrop';
        backdrop.innerHTML = `
            <div class="rll-modal">
                <button class="rll-modal-close">&times;</button>
                <div class="rll-modal-video">
                    <iframe src="https://www.youtube.com/embed/${esc(videoId)}?autoplay=${autoplay}&fs=${fsParam}&enablejsapi=1"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture"
                        allowfullscreen
                        title="${esc(title)}"></iframe>
                    ${!isMobile ? `<button class="rll-fullscreen-btn" aria-label="Toggle fullscreen">${maximizeSvg}</button>` : ''}
                    ${scoreBug ? renderScoreBug(scoreBug, 896) : ''}
                </div>
            </div>
        `;

        // Resize score bug in modal based on actual modal width
        if (scoreBug) {
            requestAnimationFrame(() => {
                const modalVideo = backdrop.querySelector('.rll-modal-video') as HTMLElement;
                if (modalVideo) {
                    const width = modalVideo.offsetWidth;
                    const bug = modalVideo.querySelector('.rll-score-bug') as HTMLElement;
                    if (bug) {
                        const scale = Math.min(1.2, Math.max(0.4, width / SCORE_BUG_REFERENCE_WIDTH));
                        bug.style.transform = `scale(${scale})`;
                    }
                }
            });
        }

        // Make the score bug timecode-aware: fetch the game's state timeline and
        // drive the bug from the player's playback position (via the YouTube
        // IFrame API) so it shows the score AT that point in the video — live,
        // on archive replay, and while scrubbing — instead of the final score.
        // Falls back to the stored values when there's no timeline / time axis.
        let scorePollInterval: ReturnType<typeof setInterval> | null = null;
        let ytPlayer: any = null;
        if (scoreBug?.gameId) {
            fetchTimeline(scoreBug.gameId).then(timeline => {
                const hasTimeAxis = !!timeline && timeline.transitions.some(t => t.t != null);
                if (!hasTimeAxis) return;
                const bugEl = backdrop.querySelector('.rll-score-bug') as HTMLElement | null;
                const iframe = backdrop.querySelector('.rll-modal-video iframe') as HTMLIFrameElement | null;
                if (!bugEl || !iframe) return;
                loadYouTubeApi().then(() => {
                    if (!backdrop.isConnected) return; // modal already closed
                    try {
                        ytPlayer = new (window as any).YT.Player(iframe, {});
                    } catch (_) {
                        return; // player handshake failed — keep the stored fallback
                    }
                    scorePollInterval = setInterval(() => {
                        try {
                            const pos = ytPlayer?.getCurrentTime?.();
                            if (typeof pos === 'number' && !Number.isNaN(pos)) {
                                bugEl.innerHTML = scoreBugInner(scoreBug, stateAt(timeline!, pos));
                            }
                        } catch (_) { /* not ready yet / torn down */ }
                    }, 250);
                });
            });
        }

        const cleanupScoreTracking = () => {
            if (scorePollInterval) { clearInterval(scorePollInterval); scorePollInterval = null; }
            try { ytPlayer?.destroy?.(); } catch (_) { /* already gone */ }
            ytPlayer = null;
        };

        // Fullscreen toggle on the container so score bug stays visible (desktop only).
        // On mobile we rely on YouTube's native fullscreen button instead.
        const modalVideo = backdrop.querySelector('.rll-modal-video') as HTMLElement;
        const fsBtn = modalVideo?.querySelector('.rll-fullscreen-btn') as HTMLButtonElement;
        let isFsActive = false;
        const updateFsIcon = () => {
            if (fsBtn) fsBtn.innerHTML = isFsActive ? minimizeSvg : maximizeSvg;
        };
        if (fsBtn && modalVideo) {
            fsBtn.addEventListener('click', () => {
                if (isFsActive) {
                    const fsEl = document.fullscreenElement || (document as any).webkitFullscreenElement;
                    if (fsEl) {
                        if (document.exitFullscreen) document.exitFullscreen();
                        else if ((document as any).webkitExitFullscreen) (document as any).webkitExitFullscreen();
                    }
                    modalVideo.classList.remove('rll-css-fullscreen');
                    isFsActive = false;
                    updateFsIcon();
                } else {
                    isFsActive = true;
                    modalVideo.classList.add('rll-css-fullscreen');
                    updateFsIcon();
                    try {
                        const p = modalVideo.requestFullscreen
                            ? modalVideo.requestFullscreen()
                            : (modalVideo as any).webkitRequestFullscreen
                                ? (modalVideo as any).webkitRequestFullscreen()
                                : null;
                        if (p && p.catch) p.catch(() => {});
                    } catch { /* CSS fallback handles it */ }
                }
            });
        }
        const handleFsChange = () => {
            const fsEl = document.fullscreenElement || (document as any).webkitFullscreenElement;
            if (!fsEl) {
                isFsActive = false;
                modalVideo.classList.remove('rll-css-fullscreen');
                updateFsIcon();
            }
        };
        document.addEventListener('fullscreenchange', handleFsChange);
        document.addEventListener('webkitfullscreenchange', handleFsChange);

        backdrop.addEventListener('click', (e) => {
            if (e.target === backdrop || (e.target as HTMLElement).closest('.rll-modal-close')) {
                // Exit fullscreen if active before closing
                if (isFsActive) {
                    const fsEl = document.fullscreenElement || (document as any).webkitFullscreenElement;
                    if (fsEl) {
                        if (document.exitFullscreen) document.exitFullscreen();
                        else if ((document as any).webkitExitFullscreen) (document as any).webkitExitFullscreen();
                    }
                }
                document.removeEventListener('fullscreenchange', handleFsChange);
                document.removeEventListener('webkitfullscreenchange', handleFsChange);
                cleanupScoreTracking();
                backdrop.remove();
            }
        });

        shadowRoot.appendChild(backdrop);
    }

    // --- Responsive resize ---
    function setupResizeObserver() {
        if (typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(entries => {
            for (const entry of entries) {
                const width = entry.contentRect.width;
                const newCount = computeVisibleThumbnails(width);
                if (newCount !== visibleThumbnails) {
                    visibleThumbnails = newCount;
                    currentPage = 0;
                    render();
                }
            }
        });
        observer.observe(rootEl);
    }

    // --- Initialize ---
    async function init() {
        // Create container next to the script tag
        const container = document.createElement('div');
        container.id = `rll-widget-${widgetId}`;
        currentScript.parentNode?.insertBefore(container, currentScript);

        // Shadow DOM for style isolation
        shadowRoot = container.attachShadow({ mode: 'open' });

        // Root element
        rootEl = document.createElement('div');
        rootEl.id = 'rll-widget-root';
        shadowRoot.appendChild(rootEl);

        showLoading();

        try {
            const [settingsData, widgetData] = await Promise.all([
                fetchSettings(),
                fetchData()
            ]);

            settings = settingsData;
            channelInfo = widgetData.channelInfo;
            divisions = (widgetData.divisions || []).sort((a: Division, b: Division) => a.display_order - b.display_order);
            activeStreams = widgetData.activeStreams || [];
            scoreData = widgetData.scoreData || {};
            tabVideos['live'] = activeStreams;
            visibleThumbnails = settings.behavior.thumbnailsPerPage;

            // Inject styles
            const style = document.createElement('style');
            style.textContent = generateCSS();
            shadowRoot.insertBefore(style, rootEl);

            render();
            setupResizeObserver();

            // Poll for active streams and scores every 30 seconds
            setInterval(async () => {
                try {
                    const data = await fetchData();
                    activeStreams = data.activeStreams || [];
                    // Merge (don't replace) so archived score data loaded from
                    // opened division tabs survives the live-stream refresh.
                    scoreData = { ...scoreData, ...(data.scoreData || {}) };
                    tabVideos['live'] = activeStreams;
                    if (currentTab === 'live') render();
                } catch (_) { /* silent */ }
            }, 30000);

        } catch (error) {
            console.error('RLL Widget: Failed to initialize', error);
            showError();
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
